<template>
  <DialogTemplateToolkit
    :data-qa="dataQa"
    :dialog.sync="dialog"
    second_btn="Common.Close"
    :title="$t('User.PaymentAccountParameters')"
    width="540"
    without_first_btn
    @close="cancel">
    <template #activator-btn>
      <MenuItem
        v-if="mode==='list'"
        class="h7-text-medium"
        :data-qa="`${dataQa}-list-item-create-invoice`"
        :disabled="$acl.check('noAccess')"
        icon="draft"
        icon-color="grey"
        :label="bigFirstLetter($t('User.CreateInvoice'))"
        @click="open"/>
      <sButton
        v-else
        :data-qa="`${dataQa}-btn-create-invoice`"
        :disabled="$acl.check('noAccess')"
        :loading="loading"
        :rounded="mode==='black-button'"
        :type="mode==='black-button' ? 'black' : 'text'"
        @click="open">
        {{ $t('User.CreateInvoice') }}
      </sButton>
    </template>
    <template #card-text>
      <div>
        <div class="px-3 flex mb-6">
          <div class="width-50">
            <KeyValue
              :caption="$t('User.CurrentBalance')"
              class="mb-6"
              :data-qa="`${dataQa}-current-balance`">
              <div :class="!settings || settings.balance < 0 ? 'text-error' : 'black--text'">
                {{ settings ? settings.balance : '-' }} {{ curency_by_tariff }}
              </div>
            </KeyValue>
            <KeyValue
              :caption="$t('User.AccountUnderContract')"
              class="pb-1"
              :data-qa="`${dataQa}-current-balance`">
              <v-skeleton-loader
                v-if="loading"
                :boilerplate="false"
                :tile="false"
                type="text"/>
              <div
                v-else
                class="text-left h7-text black--text"
                :data-qa="`${dataQa}-account-under-contract`">
                {{ contract ? contract : '-' }}
              </div>
            </KeyValue>
          </div>
          <KeyValue
            :caption="$t('User.CurrentTariff')"
            class="width-50"
            :data-qa="`${dataQa}-current-tariff`">
            <v-skeleton-loader
              v-if="loading"
              :boilerplate="false"
              :tile="false"
              type="text"/>
            <div
              v-else
              class="text-left h7-text black--text">
              {{ tariff ? `${tariff} ${curency_by_tariff} /  ${smallFirstLetter($t('Purchasing.Month'))} ${smallFirstLetter($t('Tenders.WithVAT'))}` : '-' }}
            </div>
          </KeyValue>
        </div>
        <div class="px-1 flex wrap align-start">
          <div class="h9-label px-2 pb-1">
            {{ $t('User.SelectPaymentPeriod') }}
          </div>
          <MonthPicker
            class="width-100"
            :data-qa="`${dataQa}-month-picker`"
            :is-month-available="checkMonthAvailable"
            :max-date="max_month"
            :min-date="min_month"
            :period.sync="period"
            range/>
        </div>
        <sAlert
          small>
          {{ $t('User.AmountOfAccountFormation') }}
          <div slot="desc">
            <template v-if="amount_by_tariff">
              {{ `${amount_by_tariff} ${curency_by_tariff}` }}
            </template>
            <template v-else>
              -
            </template>
          </div>
        </sAlert>
      </div>
    </template>
    <template #first_btn>
      <sButton
        :data-qa="`${dataQa}-btn-create-invoice`"
        :disabled="isCreateInvoiceDisabled"
        :loading="load_invoice"
        @click="submit">
        {{ $t('User.CreateInvoice') }}
      </sButton>
    </template>
  </DialogTemplateToolkit>
</template>

<script>
import MenuItem from 'components/common/MenuItem.vue'
import KeyValue from 'components/common/KeyValue.vue'
import MonthPicker from 'components/common/elements/MonthPicker.vue'
import DialogTemplateToolkit from 'components/common/dialogs/DialogTemplateToolkit.vue'

import moment from 'moment'
import 'moment-precise-range-plugin'

import { modalMixin } from 'mixins'
import { mapGetters } from 'vuex'
import SettingsApi from '@/api/settingsApi'
const settingsApi = new SettingsApi()

export default {
  name: 'createInvoiceDialog',
  mixins: [ modalMixin ],
  data() {
    return {
      dialog: false,
      loading: false,
      load_invoice: false,
      period: [null, null],
      date: moment().format('YYYY-MM-DD'),
      min_month: moment().format('YYYY-MM'),
      max_month: moment().add(10, 'years').format('YYYY-MM'),
      contract: 'SLS-2020-001',
      tariffs: [],
      currency: {},
      monthAvailableCache: {},
      formedRequestKeys: [], // ON: ключи уже успешно сформованных запросов (защита от дублей при повторном submit)
    }
  },
  watch: {
    period() {
      this.formedRequestKeys = []
    },
  },
  props: {
    mode: {
      type: String,
      validator: function (value) {
        const correct = ['button', 'list', 'black-button'].indexOf(value) !== -1
        return correct
      },
      default: 'button'
    },
    dataQa: {
      type: String,
      default: 'create-invoice-dialog'
    }
  },
  filters: {
    formDate(val) {
      if (val) {
        return moment(val).format('DD.MM.YYYY')
      }
    }
  },
  computed: {
    ...mapGetters({
      settings: 'getSettings',
    }),
    tariff() {
      if (!Array.isArray(this.tariffs) || this.tariffs.length === 0) {
        return 0
      }
      return this.tariffs[0].tariff
    },
    /**
     * Requests to send for the selected period, or null when the tariffs do not fully cover it.
     * CONTRACT: result of buildTariffRequests(this.period, this.tariffs).
     */
    tariffRequests() {
      return this.buildTariffRequests(this.period, this.tariffs)
    },
    /** True only when tariffRequests is a non-empty array (full coverage). */
    canFormInvoice() {
      return !!this.tariffRequests && this.tariffRequests.length > 0
    },
    /**
     * CONTRACT: true only when growthBook.invoice_tariff_coverage_check is truthy; a missing flag or a
     * missing growthBook means false (OFF). growthBook comes from the global mixin getter.
     */
    invoiceTariffCoverageCheck() {
      return !!(this.growthBook && this.growthBook.invoice_tariff_coverage_check)
    },
    /**
     * CONTRACT: ON -> !canFormInvoice, OFF -> !amount_by_tariff, in both cases || loading.
     */
    isCreateInvoiceDisabled() {
      const blocked = this.invoiceTariffCoverageCheck ? !this.canFormInvoice : !this.amount_by_tariff
      return blocked || this.loading
    },
    /**
     * CONTRACT: single source of the request list shared by amount_by_tariff and submit.
     * ON -> tariffRequests || []; OFF -> mergeTariffRanges(getMonthList()) (origin/develop behaviour).
     */
    selectedTariffMonths() {
      if (this.invoiceTariffCoverageCheck) { return this.tariffRequests || [] }
      return this.mergeTariffRanges(this.getMonthList())
    },
    amount_by_tariff() {
      if (this.invoiceTariffCoverageCheck && !this.canFormInvoice) { return 0 }
      const total = this.selectedTariffMonths.reduce((sum, item) => sum + item.amount, 0)

      if (this.settings?.balance < 0) {
        return total + (this.settings.balance * -1)
      }
      return this.$n(total)
    },
    curency_by_tariff() {
      if (!this.currency || !this.currency.shortTitle) {
        return this.$t('Tenders.hrn')
      }
      return this.currency.shortTitle
    }
  },
  methods: {
    checkMonthAvailable(month) {
      if (!this.monthAvailableCache) { this.monthAvailableCache = {} }
      if (this.monthAvailableCache[month] !== undefined) {
        return this.monthAvailableCache[month]
      }
      const m = moment(month, 'YYYY-MM')
      const available = this.invoiceTariffCoverageCheck
        ? this.tariffs.some(t => this.tariffCoversMonth(t, m.startOf('month'), moment(m).endOf('month')))
        : this.tariffs.some(t => {
          const from = moment(t.from)
          const to = moment(t.to)
          return m.isSameOrAfter(from, 'month') && m.isSameOrBefore(to, 'month')
        })
      this.monthAvailableCache[month] = available
      return available
    },
    /**
     * ON: календарный день границы тарифа. Для строки берём 'YYYY-MM-DD' без пересчёта в локальную зону
     * браузера; null/undefined -> null (открытая граница).
     */
    tariffBoundDay(value) {
      if (value === null || value === undefined || value === '') { return null }
      if (typeof value === 'string') { return moment(value.slice(0, 10), 'YYYY-MM-DD') }
      return moment(value).startOf('day')
    },
    /** ON: ОДНА запись тарифа полностью покрывает месяц (день-в-день); from == null это -inf, to == null это +inf. */
    tariffCoversMonth(t, monthStart, monthEnd) {
      const from = this.tariffBoundDay(t.from)
      const to = this.tariffBoundDay(t.to)
      return (!from || from.isSameOrBefore(monthStart, 'day')) && (!to || to.isSameOrAfter(monthEnd, 'day'))
    },
    /**
     * Pure helper. period: ['YYYY-MM', 'YYYY-MM'|undefined]; tariffs: [{ from, to, tariff, currency }].
     * Returns null if any month of the period is not fully covered by ONE tariff record
     * (day granularity: isSameOrBefore(monthStart,'day') && isSameOrAfter(endOf('month'),'day')),
     * otherwise [{ from: 'YYYY-MM', to: 'YYYY-MM', tariff, monthsCount, amount }] grouped by
     * consecutive months of the SAME tariff record (equal-price adjacent records are not merged).
     */
    buildTariffRequests(period, tariffs) {
      if (!Array.isArray(period) || !period[0] || !Array.isArray(tariffs) || tariffs.length === 0) {
        return null
      }
      const start = moment(period[0], 'YYYY-MM')
      const end = period[1] ? moment(period[1], 'YYYY-MM') : start
      const months = end.diff(start, 'months') + 1
      if (months < 1) { return null }
      const groups = []
      for (let i = 0; i < months; i++) {
        const monthStart = moment(start).add(i, 'months').startOf('month')
        const monthEnd = moment(monthStart).endOf('month')
        const record = tariffs.find(t => this.tariffCoversMonth(t, monthStart, monthEnd))
        if (!record) { return null }
        const month = monthStart.format('YYYY-MM')
        const last = groups[groups.length - 1]
        if (last && last.record === record) {
          last.to = month
          last.monthsCount++
        } else {
          groups.push({ record, from: month, to: month, monthsCount: 1 })
        }
      }
      return groups.map(g => ({
        from: g.from,
        to: g.to,
        tariff: g.record.tariff,
        monthsCount: g.monthsCount,
        amount: g.monthsCount * g.record.tariff
      }))
    },
    /**
     * CONTRACT (origin/develop, OFF path): monthList [{ month: 'YYYY-MM', tariff }] ->
     * [{ from, to, tariff, monthsCount, amount }] merging adjacent months with equal price.
     */
    mergeTariffRanges(monthList) {
      if (!monthList.length) { return [] }
      const result = []
      for (let i = 0; i < monthList.length; i++) {
        const cur = monthList[i]
        if (result.length === 0) {
          result.push({ from: cur.month, to: cur.month, tariff: cur.tariff })
        } else {
          const prev = result[result.length - 1]
          const prevTo = moment(prev.to, 'YYYY-MM')
          const curMonth = moment(cur.month, 'YYYY-MM')
          if (prev.tariff === cur.tariff && curMonth.diff(prevTo, 'months') === 1) {
            prev.to = cur.month
          } else {
            result.push({ from: cur.month, to: cur.month, tariff: cur.tariff })
          }
        }
      }
      return result.map(item => {
        const from = moment(item.from, 'YYYY-MM')
        const to = moment(item.to, 'YYYY-MM')
        const monthsCount = to.diff(from, 'months') + 1
        return {
          ...item,
          monthsCount,
          amount: monthsCount * item.tariff
        }
      })
    },
    /**
     * CONTRACT (origin/develop, OFF path): month list that skips months without a tariff
     * (month-granularity match). getMonthList() will dispatch to it when the flag is OFF.
     */
    getLegacyMonthList() {
      if (!Array.isArray(this.tariffs) || !this.period[0]) { return [] }
      const start = moment(this.period[0], 'YYYY-MM')
      const end = this.period[1] ? moment(this.period[1], 'YYYY-MM') : start
      const months = end.diff(start, 'months') + 1
      const monthList = []
      for (let i = 0; i < months; i++) {
        const currentMonth = moment(start).add(i, 'months').startOf('month')
        const tariffObj = this.tariffs.find(t => {
          const from = moment(t.from)
          const to = moment(t.to)
          return currentMonth.isSameOrAfter(from, 'month') && currentMonth.isSameOrBefore(to, 'month')
        })
        if (tariffObj) {
          monthList.push({
            month: currentMonth.format('YYYY-MM'),
            tariff: tariffObj.tariff
          })
        }
      }
      return monthList
    },
    getMonthList() {
      if (!this.invoiceTariffCoverageCheck) { return this.getLegacyMonthList() }
      if (!this.tariffRequests) { return [] }
      const list = []
      this.tariffRequests.forEach(item => {
        for (let i = 0; i < item.monthsCount; i++) {
          list.push({
            month: moment(item.from, 'YYYY-MM').add(i, 'months').format('YYYY-MM'),
            tariff: item.tariff
          })
        }
      })
      return list
    },
    buildInvoiceParams(item) {
      const from = item.from + '-01'
      const to = item.to + '-' + moment(item.to, 'YYYY-MM').daysInMonth()
      return {
        paymentPeriod: {
          from,
          to
        },
        invoiceAmount: item.amount
      }
    },

    async submit() {
      if (this.invoiceTariffCoverageCheck && !this.canFormInvoice) {
        return
      }
      this.load_invoice = true
      const on = this.invoiceTariffCoverageCheck
      const requestKey = item => `${item.from}|${item.to}|${item.tariff}`
      const items = on
        ? this.selectedTariffMonths.filter(item => !this.formedRequestKeys.includes(requestKey(item)))
        : this.selectedTariffMonths
      const requests = items.map(item => settingsApi.formInvoice(this.buildInvoiceParams(item)))
      try {
        const responses = await Promise.all(requests)
        this.load_invoice = false
        if (on) {
          responses.forEach((res, i) => {
            if (res?.success) { this.formedRequestKeys.push(requestKey(items[i])) }
          })
        }
        if (responses.every(res => res?.success)) {
          this.dialog = false
          this.$root.$emit('closeLowBilingAlert')
        }
        // Показываем уведомления с задержкой, чтобы verticalOffset корректно рассчитывался
        let delay = 0
        responses.forEach(res => {
          if (res?.formedInvoiceMessage) {
            setTimeout(() => {
              this.$notice.info({
                title: this.$t('User.InvoiceCreated'),
                message: res?.formedInvoiceMessage,
                showClose: true,
                dataQa: 'invoiceCreated',
                progress: true,
                dangerouslyUseHTMLString: true,
                clickText: res?.invoiceReportUrl ? this.$t('Tenders.Download') : null,
                onClick: res?.invoiceReportUrl ? () => {
                  window.open(res?.invoiceReportUrl)
                } : null,
                duration: 5000
              })
            }, delay)
            delay += 100 // 100мс между уведомлениями
          } else {
            // БЫЛ БАГ: частичный (или полный) отказ формирования счёта — settingsApi.formInvoice
            // сам гасит сетевые/GraphQL-ошибки внутри try/catch и резолвится с undefined, а бизнес-отказ
            // (success:false + errorMessage) тоже резолвится, а не рэджектится — Promise.all тут
            // никогда не падает. Раньше такой ответ просто пропускался: ни успешного, ни ошибочного
            // уведомления, диалог просто гас спиннер без единого объяснения — критично для money-flow
            setTimeout(() => {
              this.$notice.error({
                title: this.$t('Common.ErrorTitle'),
                message: res?.errorMessage || 'Ошибка формирования счета',
                showClose: true,
                dataQa: 'invoiceError',
                duration: 5000
              })
            }, delay)
            delay += 100
          }
        })
      } catch (e) {
        this.load_invoice = false
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          message: e?.message || 'Ошибка формирования счета',
          showClose: true,
          dataQa: 'invoiceError',
          duration: 5000
        })
      }
    },
    cancel() {
      this.dialog = false
    },
    open() {
      const year = new Date().getFullYear()
      let month = `${new Date().getMonth() + 1}`

      month = `00${month}`.slice(month.length,month.length + 3)
      this.period = [`${year}-${month}`]
      this.formedRequestKeys = []

      // this.$emit('apply')
      this.$emit('close')
      this.loading = true
      setTimeout(() => {
        this.dialog = true
      }, 100)
      settingsApi.getContractTariffs()
        .then((res) => {
          this.contract =  res?.contract ?? this.contract
          this.tariffs = res?.tariffs ?? []
          this.currency = this.tariffs[0]?.currency ?? this.currency
          this.monthAvailableCache = {} // сброс кэша при обновлении тарифов
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
  components: {
    MenuItem,
    KeyValue,
    MonthPicker,
    DialogTemplateToolkit,
  }
}
</script>
